import { TbArrowRight } from "react-icons/tb";
import { Controller, useForm } from "react-hook-form";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { Box, Button, Stack, TextInput } from "@mantine/core";
import type { InferInput } from "valibot";
import { minLength, object, pipe, string } from "valibot";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// API Key Form Values Schema
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const ApiKeyFormValuesSchema = object({
  apiKey: pipe(string(), minLength(1, "Please enter your API Key")),
});

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// API Key Form Values
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

type ApiKeyFormValues = InferInput<typeof ApiKeyFormValuesSchema>;

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// API Key Form
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

type ApiKeyFormProps = {
  readonly defaultValues?: Partial<ApiKeyFormValues>;
};

export function ApiKeyForm({ defaultValues }: ApiKeyFormProps) {
  const { handleSubmit, formState, control } = useForm<ApiKeyFormValues>({
    resolver: valibotResolver(ApiKeyFormValuesSchema),
    defaultValues: {
      apiKey: defaultValues?.apiKey ?? "",
    },
  });

  const { isSubmitting, submitCount, isValid, errors } = formState;

  const isFormInvalid = Object.keys(errors).length !== 0 || !isValid;
  const isSubmitDisabled = (submitCount > 0 && isFormInvalid) || isSubmitting;

  return (
    <Box
      component="form"
      noValidate={true}
      onSubmit={handleSubmit((values) => {
        const searchParams = new URLSearchParams();
        searchParams.set("key", values.apiKey);
        window.location.search = searchParams.toString();
      })}
    >
      <Stack>
        <Controller
          control={control}
          name="apiKey"
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              label="API Key"
              placeholder="Enter your API Key"
              error={error?.message}
              required={true}
              readOnly={isSubmitting}
            />
          )}
        />

        <Button
          type="submit"
          fullWidth={true}
          rightSection={<TbArrowRight />}
          disabled={isSubmitDisabled}
          loading={isSubmitting}
        >
          Continue
        </Button>
      </Stack>
    </Box>
  );
}
